import type { GetSurveyQuestionCorrelationsResponse, Survey } from '@engagement-web-app/common';
import { ChevronsLeftRightEllipsis } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Dialog } from '@/components/Dialog';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { Icon } from '@/components/Icon';
import { Pill } from '@/components/Pill';
import { Skeleton } from '@/components/Skeleton';
import { Table } from '@/components/Table';
import type { SortOrder } from '@/utils';
import * as styles from './QuestionCorrelationsDialog.css';

type QuestionCorrelationsDialogProps = {
    /** The question correlations are shown for. `null` keeps the dialog closed. */
    question: { id: string; text: string; categoryName: string } | null;
    categories: Survey['categories'];
    correlations: GetSurveyQuestionCorrelationsResponse['correlations'] | undefined;
    isFetching: boolean;
    isError: boolean;
    onRetry: () => void;
    onClose: () => void;
};

export function QuestionCorrelationsDialog({
    question,
    categories,
    correlations,
    isFetching,
    isError,
    onRetry,
    onClose,
}: QuestionCorrelationsDialogProps) {
    const [sortDir, setSortDir] = useState<SortOrder | null>(null);

    const sortedCorrelations = useMemo(() => {
        if (!correlations || sortDir === null) return correlations;
        return [...correlations].sort((a, b) =>
            sortDir === 'desc' ? b.coefficient - a.coefficient : a.coefficient - b.coefficient
        );
    }, [correlations, sortDir]);

    // Cycles desc -> asc -> unsorted, matching SurveyQuestionsComparisonTable.
    function handleSort() {
        setSortDir((current) => (current === null ? 'desc' : current === 'desc' ? 'asc' : null));
    }

    const categoryByQuestionId = useMemo(() => {
        return categories.reduce(
            (acc, category) => {
                category.questions.forEach((question) => {
                    acc[question.questionId] = category.name;
                });
                return acc;
            },
            {} as Record<string, string>
        );
    }, [categories]);

    return (
        <Dialog
            title={
                <>
                    <Icon icon={ChevronsLeftRightEllipsis} size="lg" />
                    <Flex align="center" gap="2">
                        <span>相関設問</span>
                        {isFetching && <Skeleton.Line height="2xl" width={40} />}
                        {correlations?.length && <span>({correlations.length})</span>}
                    </Flex>
                </>
            }
            open={question !== null}
            onOpenChange={(open) => {
                if (!open) onClose();
            }}
        >
            {question && (
                <>
                    <div className={styles.selectedQuestionLabel}>選択中設問</div>
                    <div className={styles.selectedQuestionText}>{question.text}</div>
                </>
            )}

            {isFetching && <Table.Skeleton columns={2} rows={10} />}

            <ErrorOverlay active={isError} onRetry={onRetry} message="相関設問の取得に失敗しました。" />

            {!isFetching && !isError && correlations && (
                <Table.Root>
                    <Table.Header>
                        <Table.HeaderCell>相関設問</Table.HeaderCell>
                        <Table.HeaderCell>
                            <Table.SortButton onClick={handleSort} direction={sortDir}>
                                相関係数
                            </Table.SortButton>
                        </Table.HeaderCell>
                    </Table.Header>

                    <Table.Body>
                        {sortedCorrelations?.map((correlation) => (
                            <Table.Row key={correlation.questionId}>
                                <Table.Cell className={styles.questionText}>
                                    <Flex direction="column" align="start" gap="2">
                                        <span title={correlation.questionText}>{correlation.questionText}</span>
                                        <Pill variant="quinary" weight="bold">
                                            {categoryByQuestionId[correlation.questionId]}
                                        </Pill>
                                    </Flex>
                                </Table.Cell>
                                <Table.Cell className={styles.correlationValue}>
                                    {correlation.coefficient.toFixed(2)}
                                </Table.Cell>
                            </Table.Row>
                        ))}

                        {correlations.length === 0 && <Table.EmptyRow>相関データがありません</Table.EmptyRow>}
                    </Table.Body>
                </Table.Root>
            )}
        </Dialog>
    );
}
